import React from "react";
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import {
  BsFillPersonFill,
  BsFillCreditCardFill,
  BsListCheck,
  BsFileEarmarkPlus,
  BsPersonCheckFill,
  BsQuestionSquareFill,
  BsShop,
  BsQuestionCircleFill,
  BsCashStack,
  BsCartPlus,
} from "react-icons/bs";
import { FiChevronsLeft, FiChevronsRight, FiX } from "react-icons/fi";

const NAV_ITEMS = [
  { to: "/user/dashboard", label: "Dashboard", icon: BsListCheck },
  { to: "/user/profile", label: "My Account", icon: BsFillPersonFill },
  { to: "/user/purchase-care-plan", label: "Purchase Care Plan", icon: BsCartPlus },
  { to: "/user/payments", label: "Payments", icon: BsFillCreditCardFill },
  { to: "/user/care-details", label: "User Care Details", icon: BsCashStack },
  { to: "/user/claim-history", label: "Claim Status & History", icon: BsQuestionSquareFill },
  { to: "/user/raise-claim", label: "Raise Claim", icon: BsFileEarmarkPlus },
  { to: "/user/support", label: "Support Account", icon: BsPersonCheckFill },
  { to: "/user/refer-and-earn", label: "Refer & Earn", icon: BsQuestionCircleFill },
  { to: "/user/shop", label: "Shop", icon: BsShop },
  { to: "/user/faq-help", label: "FAQ & Help", icon: BsQuestionSquareFill },
];

const SidebarNavItems = ({ collapsed, isMobile, onNavClick }) => {
  const location = useLocation();
  const showLabels = !collapsed || isMobile;

  return (
    <List sx={{ pt: 0, pb: 0 }}>
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const isActive =
          location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

        const link = (
          <ListItem
            button
            component={Link}
            to={item.to}
            onClick={onNavClick}
            className={isActive ? "user-sidebar-item--active" : ""}
            sx={{
              color: "#ebedef",
              px: showLabels ? 2 : 1,
              justifyContent: showLabels ? "flex-start" : "center",
              "&:hover, &.user-sidebar-item--active": {
                backgroundColor: "#1D201F",
                color: "#fff",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: showLabels ? 40 : 0,
                justifyContent: "center",
              }}
            >
              <Icon />
            </ListItemIcon>
            {showLabels && <ListItemText primary={item.label} />}
          </ListItem>
        );

        if (!showLabels) {
          return (
            <Tooltip key={item.to} title={item.label} placement="right" arrow>
              <span className="user-sidebar-tooltip-wrap">{link}</span>
            </Tooltip>
          );
        }

        return <React.Fragment key={item.to}>{link}</React.Fragment>;
      })}
    </List>
  );
};

const Sidebar = ({
  variant = "temporary",
  open = false,
  collapsed = true,
  onClose,
  onNavClick,
  onToggleExpanded,
  userDetails,
}) => {
  const showProfileDetails = !collapsed || variant === "temporary";

  const profileBlock = (
    <div className="user-dashboard-sidebar__profile">
      <div className="sidebar-profile-card">
        <div className="sidebar-profile-card__initials">
          <BsFillPersonFill size={22} />
        </div>
        {showProfileDetails && (
          <div className="sidebar-profile-card__info">
            <p className="sidebar-profile-card__name">Welcome</p>
            <p className="sidebar-profile-card__subtitle">
              {userDetails?.fName || "User"}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const sidebarBody = (
    <>
      <div className="user-dashboard-sidebar__header">
        {variant === "permanent" ? (
          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={onToggleExpanded}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <FiChevronsRight size={18} /> : <FiChevronsLeft size={18} />}
          </button>
        ) : (
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <FiX size={20} />
          </button>
        )}
      </div>
      <nav className="user-dashboard-sidebar__nav">
        <SidebarNavItems collapsed={collapsed} isMobile={variant === "temporary"} onNavClick={onNavClick} />
      </nav>
      {profileBlock}
    </>
  );

  if (variant === "permanent") {
    return <aside className="user-dashboard-sidebar">{sidebarBody}</aside>;
  }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant="temporary"
      PaperProps={{ className: "user-dashboard-sidebar user-dashboard-sidebar--mobile" }}
    >
      {sidebarBody}
    </Drawer>
  );
};

export default Sidebar;
